/* Owner-only partner management -- standalone, deliberately not sharing
   code with cardiagnos-app.js (a different page, a different concern,
   and no reason to pull the 4000+ line inspector-tool bundle in here
   just to reuse a couple of small patterns). Session handling mirrors
   the inspector tool's approach (a token from the worker's `login`
   action, kept in localStorage under its own key so the two pages don't
   fight over the same session slot -- an owner could plausibly have
   both open in different tabs), but every admin action below is refused
   server-side to anyone whose session isn't role='owner' regardless of
   what this page does client-side -- this file's job is the UI, not the
   real access control. */
(function(){
  const CFG = window.CARDIAGNOS_ADMIN_CONFIG || {};
  const UI = CFG.ui || {};

  function esc(s){
    return String(s == null ? '' : s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  let SESSION = null;
  try{
    const raw = localStorage.getItem('vir_admin_session');
    if(raw) SESSION = JSON.parse(raw);
  }catch(e){ SESSION = null; }

  const loginGate = document.getElementById('admin-login-gate');
  const app = document.getElementById('admin-app');
  const loginForm = document.getElementById('adminLoginForm');
  const loginErrEl = document.getElementById('adminLoginError');
  const logoutBtn = document.getElementById('adminLogoutBtn');
  const backToAppBtn = document.getElementById('adminBackToAppBtn');
  const addPartnerForm = document.getElementById('addPartnerForm');
  const addPartnerErrEl = document.getElementById('addPartnerError');
  const tableWrap = document.getElementById('partnersTableWrap');
  const signupsWrap = document.getElementById('signupsTableWrap');
  const valuationsWrap = document.getElementById('valuationsTableWrap');
  const statsWrap = document.getElementById('adminStatsWrap');
  const contentToggleBtn = document.getElementById('adminContentToggleBtn');
  const contentBadge = document.getElementById('adminContentBadge');
  const contentFeedWrap = document.getElementById('adminContentFeedWrap');
  const notifyToggleBtn = document.getElementById('adminNotifyToggleBtn');

  // "Critical information" glance strip (requested directly: the owner
  // wants the page's most load-bearing numbers visible without having to
  // scroll/count three separate tables). No new endpoint -- these are the
  // exact same partners/signups/valuations lists the tables below render,
  // just also kept here so a count can be derived after each load.
  let lastPartners = null, lastSignups = null, lastValuations = null;
  function renderStats(){
    if(!statsWrap) return;
    if(!lastPartners || !lastSignups || !lastValuations){ statsWrap.innerHTML = ''; return; }
    const activePartners = lastPartners.filter(function(p){ return p.status === 'active'; }).length;
    const awaitingActivation = lastSignups.filter(function(s){ return s.status === 'paid'; }).length;
    const suspended = lastPartners.filter(function(p){ return p.status !== 'active'; }).length;
    const cards = [
      { num: lastPartners.length, label: 'Partners', target: 'partnersSection' },
      { num: activePartners, label: 'Active', accent: true, target: 'partnersSection' },
      { num: awaitingActivation, label: 'Awaiting activation', warn: awaitingActivation > 0, target: 'signupsSection' },
      { num: lastValuations.length, label: 'Valuation leads', target: 'valuationsSection' },
    ];
    if(suspended > 0) cards[1] = { num: activePartners, label: 'Active (' + suspended + ' suspended)', accent: true, target: 'partnersSection' };
    // Each card jumps to the table it's summarizing -- "the boxes on top
    // make it clickable so it takes me to that specific section" -- via a
    // plain in-page anchor (real <a href="#id">, not a JS-only
    // scrollIntoView) so it also works as a normal link (new tab, etc).
    statsWrap.innerHTML = cards.map(function(c){
      return '<a href="#'+c.target+'" class="admin-stat-card"><div class="admin-stat-num'+(c.accent?' admin-stat-num--accent':c.warn?' admin-stat-num--warn':'')+'">'+fmtNum(c.num)+'</div><div class="admin-stat-label">'+esc(c.label)+'</div></a>';
    }).join('');
  }

  /* ---- Shared content feed ------------------------------------------
     Every photo/video a "shares content" partner has uploaded (see
     add_report_content in the mobile app / cardiagnos-app-inspector.js),
     across every partner and report, newest first. The toggle button
     starts collapsed (the feed can get long) and glows via unseenCount
     until opened, at which point mark_content_viewed clears it -- mirrors
     a normal notification-inbox pattern rather than something bespoke. */
  let contentFeedOpen = false;
  let lastContentFeed = [];
  let lastContentLastViewed = 0;
  function setGlow(unseenCount){
    if(!contentToggleBtn || !contentBadge) return;
    if(unseenCount > 0){
      contentToggleBtn.classList.add('is-glowing');
      contentBadge.textContent = String(unseenCount);
      contentBadge.style.display = 'inline-flex';
    }else{
      contentToggleBtn.classList.remove('is-glowing');
      contentBadge.style.display = 'none';
    }
  }
  /* Foreground-only OS notifications: while this tab is open (even
     backgrounded/minimized, not closed), poll every 60s and fire a real
     Notification the moment content newer than the last one we notified
     about shows up. Can't reach the owner with the tab fully closed --
     that needs a site-root-scoped service worker, which Shopify's
     CDN-subpath asset serving doesn't support without moving this
     domain's DNS onto Cloudflare (a separate decision, not this one). */
  let notifyEnabled = false;
  try{ notifyEnabled = localStorage.getItem('vir_admin_notify') === '1'; }catch(e){}
  let lastNotifiedAtMs = 0;
  try{ lastNotifiedAtMs = Number(localStorage.getItem('vir_admin_notify_at')) || 0; }catch(e){}
  let contentPollTimer = null;

  function updateNotifyBtn(){
    if(!notifyToggleBtn) return;
    if(!('Notification' in window)){
      notifyToggleBtn.textContent = UI.notifyUnsupportedLabel || "Notifications aren't supported in this browser";
      notifyToggleBtn.disabled = true;
      return;
    }
    if(Notification.permission === 'denied'){
      notifyToggleBtn.textContent = UI.notifyDeniedLabel || 'Notifications blocked';
      notifyToggleBtn.disabled = true;
      return;
    }
    if(notifyEnabled && Notification.permission === 'granted'){
      notifyToggleBtn.textContent = UI.notifyOnLabel || '🔔 Notifications on';
      notifyToggleBtn.classList.add('is-on');
      notifyToggleBtn.disabled = true;
    }else{
      notifyToggleBtn.textContent = UI.enableNotifyLabel || '🔔 Turn on notifications';
      notifyToggleBtn.classList.remove('is-on');
      notifyToggleBtn.disabled = false;
    }
  }
  function fireContentNotifications(items){
    if(!notifyEnabled || !('Notification' in window) || Notification.permission !== 'granted') return;
    const fresh = items.filter(function(i){ return i.uploadedAt > lastNotifiedAtMs; });
    if(!fresh.length) return;
    const first = fresh[0];
    const vehicle = first.summary ? [first.summary.make, first.summary.model].filter(Boolean).join(' ') : '';
    const title = fresh.length === 1
      ? [first.partnerName, vehicle].filter(Boolean).join(' — ') || (UI.notifyTitleSingle || 'New shared content')
      : (UI.notifyTitleMulti || 'New shared content') + ' (' + fresh.length + ')';
    try{
      const notif = new Notification(title, { body: UI.notifyBody || 'Tap to view it in the partner admin page.' });
      notif.onclick = function(){ window.focus(); notif.close(); };
    }catch(e){}
    lastNotifiedAtMs = Math.max.apply(null, fresh.map(function(i){ return i.uploadedAt; }));
    try{ localStorage.setItem('vir_admin_notify_at', String(lastNotifiedAtMs)); }catch(e){}
  }
  async function refreshContentBadge(){
    try{
      const data = await workerPost('list_content_feed');
      lastContentFeed = data.items || [];
      lastContentLastViewed = data.lastViewedAtMs || 0;
      setGlow(data.unseenCount || 0);
      fireContentNotifications(lastContentFeed);
    }catch(err){ /* silent -- this is just the badge, not a page the owner is looking at */ }
  }
  if(notifyToggleBtn){
    updateNotifyBtn();
    notifyToggleBtn.addEventListener('click', function(){
      if(!('Notification' in window)) return;
      Notification.requestPermission().then(function(perm){
        if(perm === 'granted'){
          notifyEnabled = true;
          try{ localStorage.setItem('vir_admin_notify', '1'); }catch(e){}
          // Baseline to "now" so turning this on doesn't immediately fire
          // a notification for every already-existing unseen item.
          lastNotifiedAtMs = Date.now();
          try{ localStorage.setItem('vir_admin_notify_at', String(lastNotifiedAtMs)); }catch(e){}
        }
        updateNotifyBtn();
      });
    });
  }
  function startContentPolling(){
    if(contentPollTimer) return;
    contentPollTimer = setInterval(refreshContentBadge, 60000);
  }
  function renderContentFeed(){
    if(!contentFeedWrap) return;
    if(!lastContentFeed.length){
      contentFeedWrap.innerHTML = '<p class="sub">'+esc(UI.contentEmpty || 'Nothing shared yet.')+'</p>';
      return;
    }
    contentFeedWrap.innerHTML = '<div class="admin-content-feed">' + lastContentFeed.map(function(item){
      const vehicle = item.summary ? [item.summary.make, item.summary.model].filter(Boolean).join(' ') : '';
      const who = [item.partnerName, vehicle].filter(Boolean).join(' — ');
      const customer = item.summary && item.summary.customerName ? item.summary.customerName : '';
      // type 'file' = any document shared from the app (PDF, scan, ...).
      const typeLabel = item.type === 'video' ? (UI.contentVideo || 'Video') : item.type === 'file' ? 'File' : (UI.contentPhoto || 'Photo');
      const thumb = item.type === 'video'
        ? '<video src="'+esc(item.url)+'" class="admin-content-thumb" muted></video>'
        : item.type === 'file'
          ? '<div class="admin-content-thumb" style="display:flex;align-items:center;justify-content:center;font-size:30px;background:#eef1f8">📄</div>'
          : '<img src="'+esc(item.url)+'" class="admin-content-thumb" alt="">';
      const isUnseen = item.uploadedAt > lastContentLastViewed;
      return '<a class="admin-content-item'+(isUnseen?' is-unseen':'')+'" href="'+esc(item.url)+'" target="_blank" rel="noopener">'
        + thumb
        + '<div class="admin-content-meta">'
          + '<div class="title">'+esc(who || typeLabel)+'</div>'
          + '<div class="sub">'+esc(typeLabel)+(customer ? ' · '+esc(customer) : '')+' · '+esc(formatDate(item.uploadedAt) || '')+'</div>'
        + '</div>'
        + '</a>';
    }).join('') + '</div>';
  }
  async function openContentFeed(){
    contentFeedOpen = !contentFeedOpen;
    if(!contentFeedOpen){
      contentFeedWrap.innerHTML = '';
      return;
    }
    contentFeedWrap.innerHTML = '<p class="sub">…</p>';
    try{
      const data = await workerPost('list_content_feed');
      lastContentFeed = data.items || [];
      lastContentLastViewed = data.lastViewedAtMs || 0;
      renderContentFeed();
      // Mark viewed AFTER rendering with the old lastViewedAtMs, so items
      // uploaded since the previous visit still show their "new" outline
      // this one time -- marking first would make everything look
      // already-seen the moment the feed opens.
      await workerPost('mark_content_viewed');
      setGlow(0);
    }catch(err){
      contentFeedWrap.innerHTML = '<p class="admin-error">'+esc((err && err.message) || UI.contentLoadError || 'Could not load.')+'</p>';
    }
  }
  if(contentToggleBtn){
    contentToggleBtn.addEventListener('click', openContentFeed);
  }

  function showApp(){
    if(loginGate) loginGate.style.display = 'none';
    if(app) app.style.display = 'block';
  }
  function showLogin(){
    if(app) app.style.display = 'none';
    if(loginGate) loginGate.style.display = 'flex';
  }
  function clearSession(){
    try{ localStorage.removeItem('vir_admin_session'); }catch(e){}
    SESSION = null;
  }

  /* ---- Admin password (second lock on partner/sign-up/valuation data) ----
     The worker refuses those actions without a short-lived adminToken
     (error 'AdminLocked'); this asks for the admin password -- or has the
     owner create one on first use -- then retries the original call. The
     token lives in sessionStorage only (gone when the tab closes). */
  const ADMIN_TOKEN_KEY = 'vir_admin_unlock';
  function readAdminToken(){
    try{
      const t = JSON.parse(sessionStorage.getItem(ADMIN_TOKEN_KEY) || 'null');
      return t && t.expiresAt > Date.now() ? t.token : null;
    }catch(e){ return null; }
  }
  function storeAdminToken(token, expiresAt){
    try{ sessionStorage.setItem(ADMIN_TOKEN_KEY, JSON.stringify({ token, expiresAt })); }catch(e){}
  }
  async function rawPost(payload){
    const res = await fetch(CFG.workerUrl, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
    });
    let data = null;
    try{ data = await res.json(); }catch(e){}
    return { res, data: data || {} };
  }
  /* Small modal form used for changing / resetting the admin password.
     onSubmit returns true when done, or an error string to show. */
  function adminDialog(opts){
    return new Promise(function(resolve){
      const wrap = document.createElement('div');
      wrap.setAttribute('style', 'position:fixed;inset:0;z-index:2147483000;background:rgba(5,10,31,.72);display:flex;align-items:center;justify-content:center;padding:16px;font-family:inherit');
      const field = 'width:100%;box-sizing:border-box;padding:12px 14px;border-radius:10px;border:1px solid #d7dbe3;font-size:16px;margin:6px 0 12px';
      wrap.innerHTML = '<form style="background:#fff;color:#0e1b3d;border-radius:16px;padding:22px;max-width:380px;width:100%;box-shadow:0 20px 60px rgba(0,0,0,.35)">'
        + '<h3 style="margin:0 0 6px;font-size:19px">' + opts.title + '</h3>'
        + '<p style="margin:0 0 14px;color:#5b6682;font-size:14px;line-height:1.45">' + opts.desc + '</p>'
        + opts.fields.map(function(f){
            return '<label style="font-size:13px;font-weight:600">' + f.label + '</label><input type="' + (f.type || 'password') + '" name="' + f.name + '" autocomplete="' + (f.autocomplete || 'off') + '"' + (f.inputmode ? ' inputmode="' + f.inputmode + '"' : '') + ' style="' + field + '">';
          }).join('')
        + '<p class="adm-err" style="color:#c0392b;font-size:13px;min-height:18px;margin:0 0 10px"></p>'
        + '<div style="display:flex;gap:10px"><button type="button" data-cancel style="flex:1;padding:12px;border-radius:10px;border:1px solid #d7dbe3;background:#fff;font-weight:700">Cancel</button>'
        + '<button type="submit" style="flex:1;padding:12px;border-radius:10px;border:0;background:#1a4fd6;color:#fff;font-weight:700">' + opts.submitLabel + '</button></div></form>';
      document.body.appendChild(wrap);
      const form = wrap.querySelector('form');
      const errEl = wrap.querySelector('.adm-err');
      form.querySelector('input').focus();
      wrap.querySelector('[data-cancel]').addEventListener('click', function(){ wrap.remove(); resolve(false); });
      form.addEventListener('submit', async function(e){
        e.preventDefault();
        errEl.textContent = '';
        const r = await opts.onSubmit(form);
        if(r === true){ wrap.remove(); resolve(true); }
        else errEl.textContent = r || 'Something went wrong.';
      });
    });
  }
  // Forgot the admin password: a code emailed to the owner + the login
  // password set a new one. Resolves to a fresh adminToken, or null.
  async function resetAdminPassword(){
    const sent = await rawPost({ action:'request_password_change_code', token: SESSION && SESSION.token });
    if(!(sent.res.ok && sent.data.ok)){ window.alert(sent.data.error || 'Could not send the code.'); return null; }
    let adminToken = null;
    const ok = await adminDialog({
      title: 'Reset admin password',
      desc: 'We emailed a 6-digit code to ' + (sent.data.emailHint || 'your email') + '. Enter it with your login password to set a new admin password.',
      fields: [
        { name:'code', label:'6-digit code', type:'text', inputmode:'numeric', autocomplete:'one-time-code' },
        { name:'acct', label:'Your login password', autocomplete:'current-password' },
        { name:'pw', label:'New admin password (min 8)', autocomplete:'new-password' },
        { name:'pw2', label:'Repeat new admin password', autocomplete:'new-password' },
      ],
      submitLabel: 'Save',
      onSubmit: async function(form){
        if(form.pw.value !== form.pw2.value) return 'The admin passwords don\'t match.';
        const r = await rawPost({ action:'set_admin_password', token: SESSION && SESSION.token,
          accountPassword: form.acct.value, resetCode: form.code.value.trim(), newAdminPassword: form.pw.value });
        if(r.res.ok && r.data.ok && r.data.adminToken){ storeAdminToken(r.data.adminToken, r.data.expiresAt); adminToken = r.data.adminToken; return true; }
        return r.data.error;
      },
    });
    return ok ? adminToken : null;
  }
  async function changeAdminPassword(){
    const ok = await adminDialog({
      title: 'Change admin password',
      desc: 'The password that opens partner and customer data. At least 8 characters, different from your login password.',
      fields: [
        { name:'cur', label:'Current admin password', autocomplete:'current-password' },
        { name:'pw', label:'New admin password (min 8)', autocomplete:'new-password' },
        { name:'pw2', label:'Repeat new admin password', autocomplete:'new-password' },
      ],
      submitLabel: 'Change',
      onSubmit: async function(form){
        if(form.pw.value !== form.pw2.value) return 'The new admin passwords don\'t match.';
        const r = await rawPost({ action:'set_admin_password', token: SESSION && SESSION.token,
          currentAdminPassword: form.cur.value, newAdminPassword: form.pw.value });
        if(r.res.ok && r.data.ok && r.data.adminToken){ storeAdminToken(r.data.adminToken, r.data.expiresAt); return true; }
        return r.data.error;
      },
    });
    if(ok) window.alert('Admin password changed.');
  }

  let adminUnlockPromise = null;
  function promptAdminUnlock(needsSetup){
    if(adminUnlockPromise) return adminUnlockPromise;
    adminUnlockPromise = new Promise(function(resolve, reject){
      const wrap = document.createElement('div');
      wrap.setAttribute('style', 'position:fixed;inset:0;z-index:2147483000;background:rgba(5,10,31,.72);display:flex;align-items:center;justify-content:center;padding:16px;font-family:inherit');
      const field = 'width:100%;box-sizing:border-box;padding:12px 14px;border-radius:10px;border:1px solid #d7dbe3;font-size:16px;margin:6px 0 12px';
      wrap.innerHTML = '<form style="background:#fff;color:#0e1b3d;border-radius:16px;padding:22px;max-width:380px;width:100%;box-shadow:0 20px 60px rgba(0,0,0,.35)">'
        + '<h3 style="margin:0 0 6px;font-size:19px">' + (needsSetup ? 'Create an admin password' : 'Admin password') + '</h3>'
        + '<p style="margin:0 0 14px;color:#5b6682;font-size:14px;line-height:1.45">' + (needsSetup
            ? 'Partner and customer data now needs a separate admin password, different from your login password.'
            : 'Enter the admin password to view partner and customer data.') + '</p>'
        + (needsSetup
            ? '<label style="font-size:13px;font-weight:600">Your login password</label><input type="password" name="acct" autocomplete="current-password" style="' + field + '">'
              + '<label style="font-size:13px;font-weight:600">New admin password (min 8)</label><input type="password" name="pw" autocomplete="new-password" style="' + field + '">'
              + '<label style="font-size:13px;font-weight:600">Repeat admin password</label><input type="password" name="pw2" autocomplete="new-password" style="' + field + '">'
            : '<input type="password" name="pw" autocomplete="off" style="' + field + '">'
              + '<button type="button" data-forgot style="background:none;border:0;padding:0 0 12px;color:#1a4fd6;font-weight:600;font-size:13px;cursor:pointer">Forgot admin password?</button>')
        + '<p class="adm-err" style="color:#c0392b;font-size:13px;min-height:18px;margin:0 0 10px"></p>'
        + '<div style="display:flex;gap:10px"><button type="button" data-cancel style="flex:1;padding:12px;border-radius:10px;border:1px solid #d7dbe3;background:#fff;font-weight:700">Cancel</button>'
        + '<button type="submit" style="flex:1;padding:12px;border-radius:10px;border:0;background:#1a4fd6;color:#fff;font-weight:700">' + (needsSetup ? 'Save' : 'Unlock') + '</button></div></form>';
      document.body.appendChild(wrap);
      const form = wrap.querySelector('form');
      const errEl = wrap.querySelector('.adm-err');
      form.querySelector('input').focus();
      function done(ok, value){ wrap.remove(); adminUnlockPromise = null; ok ? resolve(value) : reject(new Error('Admin password required.')); }
      wrap.querySelector('[data-cancel]').addEventListener('click', function(){ done(false); });
      const forgotBtn = wrap.querySelector('[data-forgot]');
      if(forgotBtn) forgotBtn.addEventListener('click', function(){
        wrap.remove();
        resetAdminPassword().then(function(t){
          adminUnlockPromise = null;
          t ? resolve(t) : reject(new Error('Admin password required.'));
        });
      });
      form.addEventListener('submit', async function(e){
        e.preventDefault();
        errEl.textContent = '';
        const pw = form.pw.value;
        if(needsSetup && pw !== form.pw2.value){ errEl.textContent = 'The admin passwords don\'t match.'; return; }
        const payload = needsSetup
          ? { action:'set_admin_password', token: SESSION && SESSION.token, accountPassword: form.acct.value, newAdminPassword: pw }
          : { action:'unlock_admin', token: SESSION && SESSION.token, adminPassword: pw };
        const r = await rawPost(payload);
        if(r.res.ok && r.data.ok && r.data.adminToken){
          storeAdminToken(r.data.adminToken, r.data.expiresAt);
          done(true, r.data.adminToken);
        }else if(r.data.needsSetup && !needsSetup){
          done(false);
          promptAdminUnlock(true).then(resolve, reject);
        }else{
          errEl.textContent = r.data.error || 'Something went wrong.';
        }
      });
    });
    return adminUnlockPromise;
  }

  async function workerPost(action, extra){
    const payload = Object.assign({ action, token: SESSION && SESSION.token }, extra || {});
    const tok = readAdminToken();
    if(tok) payload.adminToken = tok;
    let { res, data } = await rawPost(payload);
    if(res.status === 403 && data.adminLocked){
      payload.adminToken = await promptAdminUnlock(!!data.needsSetup);
      ({ res, data } = await rawPost(payload));
    }
    if(res.status === 401){
      clearSession();
      showLogin();
      throw new Error('Session expired.');
    }
    if(!res.ok || !data.ok) throw new Error((data && data.error) || 'Request failed');
    return data;
  }

  function formatDate(ms){
    if(!ms) return null;
    try{ return new Date(ms).toLocaleDateString(); }catch(e){ return null; }
  }

  // Same cities as the booking form; partners get the customers booking there.
  const PARTNER_CITIES = [['Saudi Arabia', ["Riyadh", "Jeddah", "Mecca", "Medina", "Dammam", "Al Khobar", "Dhahran", "Taif", "Tabuk", "Abha", "Buraydah", "Khamis Mushait"]], ['Sweden', ["Stockholm", "Göteborg", "Malmö", "Uppsala", "Linköping", "Västerås", "Örebro", "Helsingborg", "Norrköping", "Jönköping", "Lund", "Umeå"]]];
  function citySelectHtml(p){
    return '<select data-action="set_partner_city" data-id="'+p.id+'"'+(p.city ? '' : ' style="border-color:#c0392b"')+'>'
      + '<option value="">'+(p.city ? '' : 'Set city…')+'</option>'
      + PARTNER_CITIES.map(function(g){
          return '<optgroup label="'+esc(g[0])+'">' + g[1].map(function(c){
            return '<option value="'+esc(c)+'"'+(p.city === c ? ' selected' : '')+'>'+esc(c)+'</option>';
          }).join('') + '</optgroup>';
        }).join('')
      + (p.city && !PARTNER_CITIES.some(function(g){ return g[1].indexOf(p.city) !== -1; }) ? '<option value="'+esc(p.city)+'" selected>'+esc(p.city)+'</option>' : '')
      + '</select>';
  }

  function renderPartners(partners){
    if(!partners.length){
      tableWrap.innerHTML = '<p class="sub">—</p>';
      return;
    }
    const rows = partners.map(function(p){
      const paidText = p.lastMarkedPaidAt
        ? esc(UI.lastPaid) + ': ' + esc(formatDate(p.lastMarkedPaidAt))
        : esc(UI.neverPaid);
      const statusLabel = p.status === 'active' ? UI.statusActive : UI.statusSuspended;
      const tierLabel = p.tier === 'full' ? UI.tierFull : UI.tierNormal;
      const actions = (p.status === 'active'
        ? '<button type="button" class="btn btn-secondary" data-action="mark_partner_paid" data-id="'+p.id+'">'+esc(UI.markPaid)+'</button>'
          + '<button type="button" class="btn btn-secondary" data-action="suspend_partner" data-id="'+p.id+'">'+esc(UI.suspend)+'</button>'
        : '<button type="button" class="btn btn-secondary" data-action="reactivate_partner" data-id="'+p.id+'">'+esc(UI.reactivate)+'</button>')
        + '<button type="button" class="btn btn-secondary admin-btn-danger" data-action="delete_partner" data-id="'+p.id+'" data-confirm="'+esc(UI.deletePartnerConfirm)+'">'+esc(UI.deletePartner)+'</button>';
      return '<tr>'
        + '<td data-label="Business">'+esc(p.businessName)+'<div class="sub">'+esc(p.username)+'</div></td>'
        + '<td data-label="Contact">'+esc(p.contactEmail||'')+'<div class="sub">'+esc(p.contactPhone||'')+'</div></td>'
        + '<td data-label="Tier"><select data-action="set_partner_tier" data-id="'+p.id+'">'
          + '<option value="normal"'+(p.tier==='normal'?' selected':'')+'>'+esc(UI.tierNormal)+'</option>'
          + '<option value="full"'+(p.tier==='full'?' selected':'')+'>'+esc(UI.tierFull)+'</option>'
          + '</select></td>'
        + '<td data-label="City">'+citySelectHtml(p)+'</td>'
        + '<td data-label="Status" class="admin-status admin-status--'+esc(p.status)+'">'+esc(statusLabel)+'</td>'
        + '<td data-label="Payment" class="sub">'+paidText+'</td>'
        + '<td data-label="'+esc(UI.sharesContentLabel)+'"><label class="admin-checkbox-field admin-checkbox-field--table"><input type="checkbox" data-action="set_partner_shares_content" data-id="'+p.id+'"'+(p.sharesContent?' checked':'')+'></label></td>'
        + '<td class="admin-actions">'+actions+'</td>'
        + '</tr>';
    }).join('');
    tableWrap.innerHTML = '<table class="admin-table"><thead><tr>'
      + '<th>Business</th><th>Contact</th><th>Tier</th><th>City</th><th>Status</th><th>Payment</th><th>'+esc(UI.sharesContentLabel)+'</th><th></th>'
      + '</tr></thead><tbody>'+rows+'</tbody></table>';

    tableWrap.querySelectorAll('button[data-action], select[data-action], input[data-action]').forEach(function(el){
      const handler = function(){
        const action = el.getAttribute('data-action');
        const id = Number(el.getAttribute('data-id'));
        const confirmMsg = el.getAttribute('data-confirm');
        if(confirmMsg && !window.confirm(confirmMsg)) return;
        const extra = { partnerId: id };
        if(action === 'set_partner_tier') extra.tier = el.value;
        if(action === 'set_partner_city'){ if(!el.value) return; extra.city = el.value; }
        if(action === 'set_partner_shares_content') extra.sharesContent = el.checked;
        el.disabled = true;
        workerPost(action, extra).then(loadPartners).catch(function(err){
          alert((err && err.message) || 'Something went wrong.');
          el.disabled = false;
        });
      };
      if(el.tagName === 'SELECT' || el.type === 'checkbox') el.addEventListener('change', handler);
      else el.addEventListener('click', handler);
    });
  }

  async function loadPartners(){
    try{
      const data = await workerPost('list_partners');
      lastPartners = data.partners || [];
      renderPartners(lastPartners);
      renderStats();
    }catch(err){
      tableWrap.innerHTML = '<p class="admin-error">'+esc((err && err.message) || UI.loadError)+'</p>';
    }
  }

  const SIGNUP_STATUS_LABEL = {
    awaiting_payment: function(){ return UI.signupAwaitingPayment; },
    paid: function(){ return UI.signupPaid; },
    activated: function(){ return UI.signupActivated; },
    expired: function(){ return UI.signupExpired; },
  };
  const SIGNUP_STATUS_CLASS = { paid: 'active', activated: 'active', awaiting_payment: 'suspended', expired: 'suspended' };

  function renderSignups(signups){
    if(!signupsWrap) return;
    if(!signups.length){
      signupsWrap.innerHTML = '<p class="sub">'+esc(UI.signupsEmpty)+'</p>';
      return;
    }
    const rows = signups.map(function(s){
      const tierLabel = s.tier === 'full' ? UI.tierFull : UI.tierNormal;
      const shareLabel = s.sharesContent ? UI.signupsShares : UI.signupsNoShare;
      const statusLabel = (SIGNUP_STATUS_LABEL[s.status] || function(){ return s.status; })();
      const statusClass = SIGNUP_STATUS_CLASS[s.status] || 'suspended';
      // Backup actions only make sense while the partner has paid but not
      // yet activated -- before payment there's nothing to send, after
      // activation the account already exists.
      let actions = '<span class="sub">—</span>';
      if(s.status === 'paid'){
        actions = '<button type="button" class="btn btn-secondary" data-signup-action="copy" data-id="'+s.id+'">'+esc(UI.copyLink)+'</button>'
          + '<button type="button" class="btn btn-secondary" data-signup-action="resend" data-id="'+s.id+'">'+esc(UI.resendEmail)+'</button>';
      }
      return '<tr>'
        + '<td data-label="Business">'+esc(s.businessName)+'<div class="sub">'+esc(s.contactName||'')+'</div></td>'
        + '<td data-label="Contact">'+esc(s.contactEmail||'')+'<div class="sub">'+esc(s.contactPhone||'')+(s.city ? ' · '+esc(s.city) : '')+'</div></td>'
        + '<td data-label="Tier">'+esc(tierLabel)+'<div class="sub">'+esc(shareLabel)+'</div></td>'
        + '<td data-label="Status" class="admin-status admin-status--'+esc(statusClass)+'">'+esc(statusLabel)
          + (s.shopifyOrderName ? '<div class="sub">'+esc(s.shopifyOrderName)+'</div>' : '') + '</td>'
        + '<td class="admin-actions">'+actions+'</td>'
        + '</tr>';
    }).join('');
    signupsWrap.innerHTML = '<table class="admin-table"><thead><tr>'
      + '<th>Business</th><th>Contact</th><th>Tier</th><th>Status</th><th></th>'
      + '</tr></thead><tbody>'+rows+'</tbody></table>';

    signupsWrap.querySelectorAll('button[data-signup-action]').forEach(function(el){
      el.addEventListener('click', function(){
        const act = el.getAttribute('data-signup-action');
        const id = Number(el.getAttribute('data-id'));
        const original = el.textContent;
        el.disabled = true;
        if(act === 'resend'){
          workerPost('resend_signup_link', { signupId: id }).then(function(){
            el.textContent = UI.emailSent || 'Sent';
            setTimeout(function(){ el.textContent = original; el.disabled = false; }, 2500);
          }).catch(function(err){
            alert((err && err.message) || 'Something went wrong.');
            el.disabled = false;
          });
          return;
        }
        // copy
        workerPost('get_signup_link', { signupId: id }).then(function(data){
          const url = data && data.activationUrl;
          const done = function(){
            el.textContent = UI.linkCopied || 'Copied';
            setTimeout(function(){ el.textContent = original; el.disabled = false; }, 2500);
          };
          if(url && navigator.clipboard && navigator.clipboard.writeText){
            navigator.clipboard.writeText(url).then(done, function(){ window.prompt('Activation link:', url); done(); });
          } else {
            window.prompt('Activation link:', url || '');
            el.textContent = original; el.disabled = false;
          }
        }).catch(function(err){
          alert((err && err.message) || 'Something went wrong.');
          el.disabled = false;
        });
      });
    });
  }

  async function loadSignups(){
    if(!signupsWrap) return;
    try{
      const data = await workerPost('list_signups');
      lastSignups = data.signups || [];
      renderSignups(lastSignups);
      renderStats();
    }catch(err){
      signupsWrap.innerHTML = '<p class="admin-error">'+esc((err && err.message) || UI.loadError)+'</p>';
    }
  }

  function fmtNum(n){ if(n == null) return '—'; try{ return Number(n).toLocaleString('en-US'); }catch(e){ return String(n); } }
  const VAL_SPEC_LABEL = { gcc: 'GCC/Saudi', american: 'American', other: 'Other' };

  function renderValuations(rows){
    if(!valuationsWrap) return;
    if(!rows.length){
      valuationsWrap.innerHTML = '<p class="sub">'+esc(UI.valuationsEmpty || '—')+'</p>';
      return;
    }
    const body = rows.map(function(v){
      const car = [v.year, v.make, v.model].filter(Boolean).join(' ');
      const detail = [
        v.transmission,
        v.mileageKm != null ? fmtNum(v.mileageKm) + ' km' : null,
        v.condition,
        VAL_SPEC_LABEL[v.specOrigin] || v.specOrigin,
        v.city
      ].filter(Boolean).join(' · ');
      const contact = esc(v.contactEmail || '') + (v.contactEmail && v.contactPhone ? '<div class="sub">' : '')
        + (v.contactPhone ? esc(v.contactPhone) : '') + (v.contactEmail && v.contactPhone ? '</div>' : '');
      const est = fmtNum(v.mid) + ' ' + esc(v.currency || 'SAR')
        + '<div class="sub">' + fmtNum(v.low) + ' – ' + fmtNum(v.high) + '</div>';
      const statusLine = v.purchasedAt
        ? '<span class="admin-status admin-status--active">Purchased</span>'
        : (v.followupSentAt
            ? '<span class="sub">'+esc(UI.followupSentLabel || 'Follow-up sent')+' · '+esc(formatDate(v.followupSentAt) || '')+'</span>'
            : '<span class="sub">'+esc(UI.followupNotSentLabel || 'Not sent yet')+'</span>');
      // Not purchased -> can always (re)send manually, regardless of
      // whether the automatic pass already ran for this lead -- "if it
      // didn't send automatically for some reason" is exactly the case a
      // disabled/hidden button here wouldn't help with.
      const followupBtn = v.purchasedAt ? '' :
        '<button type="button" class="btn btn-secondary" data-action="send_valuation_followup_now" data-lead-id="'+v.id+'" style="margin-top:6px">'
        + esc(v.followupSentAt ? (UI.resendFollowupBtn || 'Resend') : (UI.sendFollowupBtn || 'Send now'))
        + '</button>';
      const status = statusLine + followupBtn;
      const sellBadge = v.sellingIntent
        ? '<span class="admin-status admin-status--active" style="margin-top:4px;display:inline-block">'+esc(UI.sellingIntentLabel || 'Wants to sell')+'</span>' : '';
      return '<tr>'
        + '<td data-label="Car">'+esc(car)+'<div class="sub">'+esc(detail)+'</div>'+sellBadge+'</td>'
        + '<td data-label="Contact">'+contact+'</td>'
        + '<td data-label="Estimate">'+est+'</td>'
        + '<td data-label="Confidence" class="sub">'+esc(v.confidence || '')+'</td>'
        + '<td data-label="Follow-up">'+status+'</td>'
        + '<td data-label="When" class="sub">'+esc(formatDate(v.createdAt) || '')+'</td>'
        + '</tr>';
    }).join('');
    valuationsWrap.innerHTML = '<table class="admin-table"><thead><tr>'
      + '<th>Car</th><th>Contact</th><th>Estimate</th><th>Confidence</th><th>Follow-up</th><th>When</th>'
      + '</tr></thead><tbody>'+body+'</tbody></table>';
    valuationsWrap.querySelectorAll('[data-action="send_valuation_followup_now"]').forEach(function(btn){
      btn.addEventListener('click', function(){
        btn.disabled = true;
        btn.textContent = UI.sendingFollowupBtn || 'Sending…';
        workerPost('send_valuation_followup_now', { leadId: Number(btn.getAttribute('data-lead-id')) })
          .then(loadValuations)
          .catch(function(err){
            alert((err && err.message) || 'Something went wrong.');
            btn.disabled = false;
            btn.textContent = UI.resendFollowupBtn || 'Resend';
          });
      });
    });
  }

  async function loadValuations(){
    if(!valuationsWrap) return;
    try{
      const data = await workerPost('list_valuations');
      lastValuations = data.valuations || [];
      renderValuations(lastValuations);
      renderStats();
    }catch(err){
      valuationsWrap.innerHTML = '<p class="admin-error">'+esc((err && err.message) || UI.loadError)+'</p>';
    }
  }

  const runFollowupsBtn = document.getElementById('adminRunFollowupsBtn');
  const runFollowupsStatus = document.getElementById('adminRunFollowupsStatus');
  if(runFollowupsBtn){
    runFollowupsBtn.addEventListener('click', async function(){
      runFollowupsBtn.disabled = true;
      if(runFollowupsStatus) runFollowupsStatus.textContent = (UI.sendingFollowupBtn || 'Sending…');
      try{
        const data = await workerPost('run_valuation_followups');
        if(runFollowupsStatus){
          runFollowupsStatus.textContent = data.enabled
            ? 'Sent ' + fmtNum(data.sentThisRun) + ' this run (' + fmtNum(data.totalSent) + ' total).'
            : 'Note: VALUATION_FOLLOWUP_ENABLED is off, so the automatic pass is disabled — this manual run still respects the same 24h–30d window. Use "Send now" on an individual lead to bypass it.';
        }
        loadValuations();
      }catch(err){
        if(runFollowupsStatus) runFollowupsStatus.textContent = (err && err.message) || 'Something went wrong.';
      }finally{
        runFollowupsBtn.disabled = false;
      }
    });
  }

  if(loginForm){
    loginForm.addEventListener('submit', async function(e){
      e.preventDefault();
      const u = document.getElementById('adminLoginUser').value.trim();
      const p = document.getElementById('adminLoginPass').value;
      if(loginErrEl) loginErrEl.style.display = 'none';
      try{
        const res = await fetch(CFG.workerUrl, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'login', username: u, password: p }),
        });
        const data = await res.json();
        if(!res.ok || !data.ok) throw new Error((data && data.error) || 'Sign-in failed');
        if(data.role !== 'owner'){
          throw new Error(UI.loginRoleError || 'This page is for the store owner only.');
        }
        // Captures the same fields cardiagnos-app.js's own login handler
        // does (not just token/role) so writeInspectorSessionHandoff()
        // below can hand a genuinely complete owner session over to the
        // inspector tool page, not a partial one.
        SESSION = {
          token: data.token, role: data.role, tier: data.tier, username: data.username,
          partnerId: data.partnerId, businessName: data.businessName, logoUrl: data.logoUrl,
        };
        try{ localStorage.setItem('vir_admin_session', JSON.stringify(SESSION)); }catch(err){}
        showApp();
        loadPartners();
        loadSignups();
        loadValuations();
        loadPartnerCards();
        loadMessageTemplates();
        refreshContentBadge();
        startContentPolling();
      }catch(err){
        if(loginErrEl){ loginErrEl.textContent = (err && err.message) || 'Sign-in failed.'; loginErrEl.style.display = 'block'; }
      }
    });
  }

  // "Partner admin" on the app's own Home screen navigates this SAME
  // WebView here (window.location.href, not a new window/tab), and the
  // app has no native top bar of its own -- so without this, tapping it
  // stranded the user with no way back to the inspection flow at all.
  // window.ReactNativeWebView only exists inside that WebView (it's what
  // InspectorWebView.tsx's own postMessage handoff relies on elsewhere in
  // this file), so this button only ever appears there, never on a normal
  // browser visit to this page.
  if(backToAppBtn && window.ReactNativeWebView){
    backToAppBtn.style.display = '';
    backToAppBtn.addEventListener('click', function(){
      window.location.href = '/pages/app-inspector';
    });
  }

  // "Change admin password" next to Sign out in the page header.
  if(logoutBtn && !document.getElementById('adminChangePwBtn')){
    const cpBtn = document.createElement('button');
    cpBtn.id = 'adminChangePwBtn';
    cpBtn.type = 'button';
    cpBtn.className = logoutBtn.className;
    cpBtn.textContent = 'Change admin password';
    cpBtn.addEventListener('click', changeAdminPassword);
    logoutBtn.parentNode.insertBefore(cpBtn, logoutBtn);
  }
  if(logoutBtn){
    logoutBtn.addEventListener('click', function(){
      if(SESSION && SESSION.token && CFG.workerUrl){
        fetch(CFG.workerUrl, {
          method:'POST', headers:{'Content-Type':'application/json'},
          body: JSON.stringify({ action:'logout', token: SESSION.token }),
        }).catch(function(){});
      }
      clearSession();
      showLogin();
    });
  }

  // Change password -- own account (role='owner' here, always), same two-
  // step code-verification flow as cardiagnos-app-inspector.js's own
  // Home > Change password screen (request_password_change_code emails a
  // 6-digit code to the account's contact address, then change_password
  // takes that code + the new password). Kept as a single always-visible
  // card rather than a modal-triggered flow -- this page has no other
  // multi-step interaction to borrow a pattern from, and a plain inline
  // form is simplest on a phone screen.
  const adminCpRequestBtn = document.getElementById('adminCpRequestBtn');
  const adminCpForm = document.getElementById('adminCpForm');
  const adminCpRequestRow = document.getElementById('adminCpRequestRow');
  const adminCpErrEl = document.getElementById('adminCpError');
  const adminCpSuccessEl = document.getElementById('adminCpSuccess');
  const adminCpSubEl = document.getElementById('adminCpSub');
  if(adminCpRequestBtn){
    adminCpRequestBtn.addEventListener('click', async function(){
      adminCpErrEl.style.display = 'none';
      adminCpRequestBtn.disabled = true;
      try{
        const data = await workerPost('request_password_change_code');
        if(adminCpSubEl && data.emailHint) adminCpSubEl.textContent = 'Code sent to ' + data.emailHint + '.';
        adminCpRequestRow.style.display = 'none';
        adminCpForm.style.display = '';
      }catch(err){
        adminCpErrEl.textContent = (err && err.message) || UI.cpGenericError || 'Something went wrong.';
        adminCpErrEl.style.display = 'block';
        adminCpRequestBtn.disabled = false;
      }
    });
  }
  if(adminCpForm){
    adminCpForm.addEventListener('submit', async function(e){
      e.preventDefault();
      adminCpErrEl.style.display = 'none';
      const submitBtn = document.getElementById('adminCpSubmitBtn');
      submitBtn.disabled = true;
      try{
        await workerPost('change_password', {
          code: document.getElementById('adminCpCode').value.trim(),
          newPassword: document.getElementById('adminCpNew').value,
        });
        adminCpForm.style.display = 'none';
        adminCpSuccessEl.style.display = 'block';
      }catch(err){
        adminCpErrEl.textContent = (err && err.message) || UI.cpGenericError || 'Something went wrong.';
        adminCpErrEl.style.display = 'block';
        submitBtn.disabled = false;
      }
    });
  }

  if(addPartnerForm){
    addPartnerForm.addEventListener('submit', async function(e){
      e.preventDefault();
      if(addPartnerErrEl) addPartnerErrEl.style.display = 'none';
      const submitBtn = addPartnerForm.querySelector('button[type="submit"]');
      if(submitBtn) submitBtn.disabled = true;
      try{
        await workerPost('create_partner', {
          businessName: document.getElementById('apBusinessName').value.trim(),
          username: document.getElementById('apUsername').value.trim(),
          password: document.getElementById('apPassword').value,
          contactEmail: document.getElementById('apEmail').value.trim(),
          contactPhone: document.getElementById('apPhone').value.trim(),
          tier: document.getElementById('apTier').value,
          accountKind: document.getElementById('apKind') ? document.getElementById('apKind').value : 'inspector',
          sharesContent: document.getElementById('apSharesContent').checked,
          city: document.getElementById('apCity') ? document.getElementById('apCity').value : '',
        });
        addPartnerForm.reset();
        loadPartners();
      }catch(err){
        if(addPartnerErrEl){ addPartnerErrEl.textContent = (err && err.message) || 'Could not create that partner.'; addPartnerErrEl.style.display = 'block'; }
      } finally {
        if(submitBtn) submitBtn.disabled = false;
      }
    });
  }

  /* ---------------- Partners & customers ----------------
     Owner-only browsing of what each partner has actually produced --
     the table above only manages partner ACCOUNTS. Two-level view: a
     grid of partner cards (logo + name), then that partner's reports
     (reusing list_reports_for_partner, the same action the partner's own
     Edit Reports overlay auto-loads for itself) once one is clicked. */
  const pcWrap = document.getElementById('partnerCustomersWrap');
  const contentModal = document.getElementById('reportContentModal');
  const contentBody = document.getElementById('reportContentBody');
  let pcPartners = [];

  function fmtDateShort(ms){
    if(!ms) return '';
    try{ return new Date(ms).toLocaleDateString(); }catch(e){ return ''; }
  }

  function renderPartnerCards(){
    if(!pcWrap) return;
    if(!pcPartners.length){
      pcWrap.innerHTML = '<p class="sub">'+esc(UI.pcEmpty||'—')+'</p>';
      return;
    }
    pcWrap.innerHTML = '<div class="pc-partner-grid">'+pcPartners.map(function(p){
      return '<button type="button" class="pc-partner-card" data-pc-partner="'+p.id+'">'
        + (p.logoUrl ? '<img src="'+esc(p.logoUrl)+'" alt="" class="pc-partner-logo">' : '<div class="pc-partner-logo pc-partner-logo--blank"></div>')
        + '<span>'+esc(p.businessName)+'</span>'
        + '</button>';
    }).join('')+'</div>';
    pcWrap.querySelectorAll('[data-pc-partner]').forEach(function(btn){
      btn.addEventListener('click', function(){
        const id = Number(btn.getAttribute('data-pc-partner'));
        const partner = pcPartners.find(function(p){ return p.id === id; });
        openPartnerReports(id, partner ? partner.businessName : '');
      });
    });
  }

  async function loadPartnerCards(){
    if(!pcWrap) return;
    try{
      const data = await workerPost('list_partners');
      pcPartners = data.partners || [];
      renderPartnerCards();
    }catch(err){
      pcWrap.innerHTML = '<p class="admin-error">'+esc((err && err.message) || UI.pcLoadError)+'</p>';
    }
  }

  async function openPartnerReports(partnerId, businessName){
    pcWrap.innerHTML = '<button type="button" class="btn btn-ghost" id="pcBackBtn">'+esc(UI.pcBackToPartners)+'</button>'
      + '<h3 class="pc-reports-heading">'+esc(businessName)+'</h3>'
      + '<div id="pcReportsList"><p class="sub">…</p></div>';
    document.getElementById('pcBackBtn').addEventListener('click', renderPartnerCards);
    const listEl = document.getElementById('pcReportsList');
    try{
      const data = await workerPost('list_reports_for_partner', { partnerId: partnerId });
      const reports = data.reports || [];
      if(!reports.length){
        listEl.innerHTML = '<p class="sub">'+esc(UI.pcReportsEmpty)+'</p>';
        return;
      }
      listEl.innerHTML = '<div class="pc-report-list">'+reports.map(function(r){
        const vehicle = [r.make, r.model].filter(Boolean).join(' ') || r.customerName || '—';
        return '<div class="pc-report-row">'
          + '<div class="pc-report-row-info"><strong>'+esc(vehicle)+'</strong>'
            + '<div class="sub">'+esc(r.reportId||'')+' · '+(r.lang==='ar'?'AR':r.lang==='sv'?'SV':'EN')+' · '+esc(fmtDateShort(r.updatedAtMs))+'</div></div>'
          + '<div class="pc-report-row-actions">'
            + '<button type="button" class="btn btn-secondary" data-pc-edit="'+encodeURIComponent(r.id)+'">'+esc(UI.pcEdit)+'</button>'
            + '<button type="button" class="btn btn-secondary" data-pc-download="'+encodeURIComponent(r.id)+'">'+esc(UI.pcDownload)+'</button>'
            + '<button type="button" class="btn btn-secondary" data-pc-content="'+esc(r.reportId||'')+'">'+esc(UI.pcContent)+'</button>'
          + '</div></div>';
      }).join('')+'</div>';
      listEl.querySelectorAll('[data-pc-edit]').forEach(function(btn){
        btn.addEventListener('click', function(){ openInspectorForReport(btn.getAttribute('data-pc-edit'), false); });
      });
      listEl.querySelectorAll('[data-pc-download]').forEach(function(btn){
        btn.addEventListener('click', function(){ downloadReportPdf(btn.getAttribute('data-pc-download'), btn); });
      });
      listEl.querySelectorAll('[data-pc-content]').forEach(function(btn){
        btn.addEventListener('click', function(){ openContentModal(btn.getAttribute('data-pc-content')); });
      });
    }catch(err){
      listEl.innerHTML = '<p class="admin-error">'+esc((err && err.message) || UI.pcLoadError)+'</p>';
    }
  }

  /* The inspector-tool page reads its own session from localStorage.vir_session,
     a deliberately separate key from this page's vir_admin_session (see the
     file header comment). Writing it here, same-origin, right before
     navigating, is what lets Edit/Download land already signed in instead of
     the inspector tool's own login gate -- requireSession re-validates the
     token server-side on every call regardless of how it got into
     localStorage, so this isn't a bypass of anything. */
  // reportGid arrives here ALREADY percent-encoded -- it's read straight
  // off the button's data-pc-edit/data-pc-download attribute, which was
  // itself set to encodeURIComponent(r.id) when the row was built (see
  // openPartnerReports). Encoding it AGAIN here (the previous version of
  // this function did) double-encodes it -- ?openReport= then carries
  // "gid%253A%252F..." instead of "gid%3A%2F...", and URLSearchParams'
  // single built-in decode on the receiving page only ever undoes ONE of
  // those two layers, so cardiagnos-app.js ends up calling get_report with
  // the id still partly percent-encoded, which Shopify's GraphQL API
  // rejects as an invalid global id. reportGid is already exactly what a
  // query string needs, so it goes in as-is.
  function openInspectorForReport(reportGid, autoDownload){
    try{ localStorage.setItem('vir_session', JSON.stringify(SESSION)); }catch(e){}
    const url = '/pages/inspector-tool?openReport=' + reportGid + (autoDownload ? '&autoDownload=1' : '');
    // This admin page can itself be loaded INSIDE the app's own WebView --
    // the owner's Home screen "Partner admin" button navigates that same
    // WebView here (see cardiagnos-app-inspector.js's #ciHomeAdmin), same
    // vir_admin_session-into-vir_session handoff idea as this function's
    // own comment describes. A plain window.location.href from THIS point
    // would navigate that SAME WebView on to cardiagnos-app.js -- a
    // completely different page with its own separate login gate -- which
    // is exactly what was reported live ("this page shouldn't appear in
    // the app"). Handing off to the in-app browser sheet instead (same one
    // Download already uses) keeps the app's own WebView on the admin page
    // untouched; this direct navigation is only correct for the case this
    // was actually written for, reaching this page on the real website
    // outside the app.
    if(window.ReactNativeWebView){
      const absoluteUrl = window.location.origin + url;
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'openInBrowser', url: absoluteUrl }));
      return;
    }
    window.location.href = url;
  }

  // Opens a report PDF so it VIEWS in the browser instead of triggering a
  // device download -- openExternal/Linking.openURL (used elsewhere for
  // things that genuinely need an OS-level handoff, like a wa.me link)
  // routed a .pdf through iOS's own Downloads manager instead of Safari's
  // inline viewer, where it was confirmed live to just stall at "Zero KB".
  // 'openInBrowser' opens the same in-app Safari view used for checkout in
  // the wrapped app (InspectorWebView.tsx), which renders a PDF inline like
  // a normal Safari tab would; outside the app, a plain top-level
  // navigation gets the same inline-viewer behavior from Safari directly
  // (window.open's new-tab/window path was the one triggering the device
  // download in a plain mobile-browser tab too).
  function openExternally(url){
    if(window.ReactNativeWebView){
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'openInBrowser', url: url }));
    } else {
      window.location.href = url;
    }
  }

  /* Download used to navigate to the inspector-tool page and re-run the
     ENTIRE report-generation wizard flow (translate fields, rasterize a
     fresh PDF, re-upload it) just to get a file that was already generated
     and uploaded once, at the moment this report was originally saved --
     exactly the way a paid customer's own PDF link works (see pdf_url on
     the cardiagnos_report metaobject, saveReportMetaobject/getReportById on
     the worker). That old path was also fragile: it depended on landing
     already signed in, on generateReport()/downloadPdf() both completing
     correctly from a simulated click chain, and (in a browser) on a delayed
     download not getting blocked for having drifted too far from the
     original click's user-activation window. Fetching pdf_url directly and
     handing THAT off is simpler and matches how every other "get this
     report's PDF" path in this codebase already works. Falls back to the
     old regenerate-from-scratch flow only if a report somehow has no
     pdf_url yet (e.g. saved through the app's offline/fallback save path
     before a PDF was ever generated for it). */
  async function downloadReportPdf(reportGid, btnEl){
    const prevLabel = btnEl ? btnEl.textContent : null;
    if(btnEl){ btnEl.disabled = true; btnEl.textContent = '…'; }
    try{
      // Unlike openInspectorForReport's URL param (which wants the already-
      // percent-encoded form), get_report's id goes straight into a JSON
      // POST body -- it needs the RAW gid, so this is the one place that
      // has to actually undo the encodeURIComponent() the button's data-
      // attribute was built with (see openInspectorForReport's own comment
      // for the double-encoding bug this exact mismatch used to cause).
      const rawId = decodeURIComponent(reportGid);
      const data = await workerPost('get_report', { id: rawId });
      const pdfUrl = data.report && data.report.pdf_url;
      if(pdfUrl){
        openExternally(pdfUrl);
      } else {
        openInspectorForReport(reportGid, true);
      }
    }catch(err){
      alert((err && err.message) || UI.pcLoadError);
    }finally{
      if(btnEl){ btnEl.disabled = false; btnEl.textContent = prevLabel; }
    }
  }

  function closeContentModal(){
    if(contentModal) contentModal.style.display = 'none';
  }
  async function openContentModal(reportId){
    if(!contentModal || !contentBody || !reportId) return;
    contentModal.style.display = 'flex';
    contentBody.innerHTML = '<p class="sub">…</p>';
    try{
      const data = await workerPost('get_report_content', { reportId: reportId });
      const items = data.content || [];
      if(!items.length){
        contentBody.innerHTML = '<p class="sub">'+esc(UI.pcContentEmpty)+'</p>';
        return;
      }
      contentBody.innerHTML = '<div class="pc-content-grid">'+items.map(function(c){
        if(c.type === 'file') return '<a href="'+esc(c.url)+'" target="_blank" rel="noopener" style="display:flex;align-items:center;gap:10px;padding:14px;border-radius:10px;margin-bottom:8px;background:rgba(79,227,255,.12);color:inherit;text-decoration:none;font-weight:600">📄 '+esc(decodeURIComponent((c.url.split('/').pop()||'File').replace(/^\d+-[0-9a-f]+-/,'')))+'</a>';
        return c.type === 'video'
          ? '<video src="'+esc(c.url)+'" controls class="pc-content-item"></video>'
          : '<img src="'+esc(c.url)+'" class="pc-content-item" alt="">';
      }).join('')+'</div>';
    }catch(err){
      contentBody.innerHTML = '<p class="admin-error">'+esc((err && err.message) || UI.pcLoadError)+'</p>';
    }
  }
  if(contentModal){
    const backdrop = contentModal.querySelector('.admin-modal-backdrop');
    const closeBtn = contentModal.querySelector('.admin-modal-close');
    if(backdrop) backdrop.addEventListener('click', closeContentModal);
    if(closeBtn) closeBtn.addEventListener('click', closeContentModal);
  }

  /* ---------------- App Message Templates (Email/WhatsApp wording) --------
     Editable per-language templates for the mobile app's own send flow
     (cardiagnos-app-inspector.js's Step 8) -- stored via the worker's
     get_message_templates/set_message_templates actions (a single shop
     metafield holding all 3 languages at once). Kept entirely separate
     from the public website's own theme-editor-based templates. */
  const mtLangSelect = document.getElementById('mtLangSelect');
  const mtWhatsapp = document.getElementById('mtWhatsapp');
  const mtEmailSubject = document.getElementById('mtEmailSubject');
  const mtEmailBody = document.getElementById('mtEmailBody');
  const mtSaveBtn = document.getElementById('mtSaveBtn');
  const mtStatus = document.getElementById('mtStatus');
  let messageTemplates = {};
  function fillMessageTemplateFields(){
    if(!mtLangSelect) return;
    const t = messageTemplates[mtLangSelect.value] || {};
    mtWhatsapp.value = t.whatsapp || '';
    mtEmailSubject.value = t.emailSubject || '';
    mtEmailBody.value = t.emailBody || '';
  }
  async function loadMessageTemplates(){
    if(!mtLangSelect) return;
    try{
      const data = await workerPost('get_message_templates');
      messageTemplates = data.templates || {};
      fillMessageTemplateFields();
    }catch(err){
      if(mtStatus) mtStatus.textContent = (err && err.message) || 'Could not load templates.';
    }
  }
  if(mtLangSelect){
    mtLangSelect.addEventListener('change', fillMessageTemplateFields);
    if(mtSaveBtn) mtSaveBtn.addEventListener('click', async function(){
      messageTemplates[mtLangSelect.value] = {
        whatsapp: mtWhatsapp.value.trim(),
        emailSubject: mtEmailSubject.value.trim(),
        emailBody: mtEmailBody.value.trim(),
      };
      mtSaveBtn.disabled = true;
      if(mtStatus) mtStatus.textContent = 'Saving…';
      try{
        await workerPost('set_message_templates', { templates: messageTemplates });
        if(mtStatus) mtStatus.textContent = 'Saved ✓';
      }catch(err){
        if(mtStatus) mtStatus.textContent = (err && err.message) || 'Could not save.';
      }finally{
        mtSaveBtn.disabled = false;
      }
    });
  }

  if(SESSION){
    showApp();
    loadPartners();
    loadSignups();
    loadValuations();
    loadPartnerCards();
    loadMessageTemplates();
    refreshContentBadge();
    startContentPolling();
  } else {
    showLogin();
  }
})();
